<div class="fi-form-container" style="display:flex; flex-direction:column; height:100%; overflow:hidden;">

  <!-- ── Stepper ──────────────────────────────────────────────────────── -->
  <mat-stepper
    #stepper
    [linear]="false"
    orientation="horizontal"
    class="fi-stepper"
    style="flex:1; overflow-y:auto; min-height:0;"
    [selectedIndex]="0">

    <!-- Step 1: Observations -->
    <mat-step label="Observations">
      <div class="px-4 py-4 overflow-y-auto">
        <form [formGroup]="form">

          <!-- Datetime picker — plain input, no mat-form-field wrapper -->
          <div class="mb-4">
            <label class="block text-xs text-gray-500 mb-1">Inspection Date &amp; Time</label>
            <input
              type="datetime-local"
              formControlName="inspectionTimestamp"
              [attr.readonly]="viewOnly ? true : null"
              class="border border-gray-300 rounded px-3 py-2 text-sm w-full max-w-xs focus:outline-none focus:ring-2 focus:ring-orange-400"
              [class.bg-gray-100]="viewOnly" />
          </div>

          <!-- Structured Observations -->
          <div *ngIf="inspectionService.config() as config">
            <p class="text-sm font-semibold text-gray-600 border-b pb-2 mb-4 uppercase tracking-wide">Structured Observations</p>

            <div *ngIf="config.structuredObservations.length === 0" class="text-sm text-gray-400 italic">
              No structured observations configured for this crop and field type.
            </div>

            <div formArrayName="structuredObservations" class="space-y-3">
              <div
                *ngFor="let obs of config.structuredObservations; let i = index"
                [formGroupName]="i"
                class="">

                <mat-form-field *ngIf="obs.fieldInputType === 'TEXT'" appearance="outline" class="w-full">
                  <mat-label>{{ obs.label }}</mat-label>
                  <input matInput formControlName="value" [readonly]="viewOnly" />
                </mat-form-field>

                <mat-form-field *ngIf="obs.fieldInputType === 'DROPDOWN'" appearance="outline" class="w-full">
                  <mat-label>{{ obs.label }}</mat-label>
                  <mat-select formControlName="value" [disabled]="viewOnly">
                    <mat-option *ngFor="let option of obs.options" [value]="option.label">
                      {{ option.label }}
                    </mat-option>
                  </mat-select>
                </mat-form-field>

              </div>
            </div>
          </div>

          <div *ngIf="inspectionService.loading() && !inspectionService.config()" class="text-sm text-gray-400 italic py-4">
            Loading form configuration...
          </div>

        </form>
      </div>
    </mat-step>

    <!-- Step 2: General -->
    <mat-step label="General">
      <div class="px-4 py-4 overflow-y-auto">
        <form [formGroup]="form">

          <p class="text-sm font-semibold text-gray-600 border-b pb-2 mb-4 uppercase tracking-wide">General Observations</p>

          <!-- Plain textarea — mat-form-field causes mdc-infix rendering bug -->
          <textarea
            formControlName="generalObservations"
            rows="5"
            [attr.readonly]="viewOnly ? true : null"
            placeholder="Record your general observations about this inspection point..."
            class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 resize-y mb-4"
            [class.bg-gray-100]="viewOnly">
          </textarea>

          <!-- Photos -->
          <div *ngIf="!viewOnly" class="mb-3">
            <p class="text-sm text-gray-600 mb-2">Photos ({{ generalPhotos.length }}/{{ MAX_GENERAL_PHOTOS }})</p>
            <label
              *ngIf="generalPhotos.length < MAX_GENERAL_PHOTOS"
              class="inline-flex items-center gap-2 cursor-pointer px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 transition-colors"
              [class.opacity-50]="uploadingPhoto"
              style="cursor: pointer;">
              <span>{{ uploadingPhoto ? 'Uploading...' : '+ Add Photo' }}</span>
              <input
                type="file"
                accept="image/*"
                style="display:none;"
                [disabled]="uploadingPhoto"
                (change)="onPhotoSelected($event)" />
            </label>
          </div>

          <div *ngIf="generalPhotos.length > 0" class="flex flex-wrap gap-3 mt-2">
            <div *ngFor="let photo of generalPhotos; let i = index"
                 class="relative w-24 h-24 rounded overflow-hidden border border-gray-200">
              <img [src]="photo.downloadUrl" alt="Inspection photo"
                   (click)="openImage(photo)"
                   matTooltip="View full size"
                   class="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity" />
              <button *ngIf="!viewOnly" type="button" (click)="removePhoto(i)"
                      class="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600">
                ×
              </button>
            </div>
          </div>

        </form>
      </div>
    </mat-step>

    <!-- Step 3: Pests -->
    <mat-step label="Pests">
      <div class="px-4 py-4 overflow-y-auto">
        <p class="text-sm font-semibold text-gray-600 border-b pb-2 mb-4 uppercase tracking-wide">Pests</p>
        <div *ngIf="inspectionService.config() as config">
          <app-findings-section
            #pestsSection
            [items]="config.pests"
            findingType="PEST"
            [viewOnly]="viewOnly"
            [tenantId]="helper.getTenantId()"
            [reportId]="reportId ?? ''"
            [preloadedFindings]="preloadedFindings"
            (changed)="onFindingsChanged()">
          </app-findings-section>
        </div>
        <div *ngIf="!inspectionService.config()" class="text-sm text-gray-400 italic py-4">No items configured for this field type.</div>
      </div>
    </mat-step>

    <!-- Step 4: Diseases -->
    <mat-step label="Diseases">
      <div class="px-4 py-4 overflow-y-auto">
        <p class="text-sm font-semibold text-gray-600 border-b pb-2 mb-4 uppercase tracking-wide">Diseases</p>
        <div *ngIf="inspectionService.config() as config">
          <app-findings-section
            #diseasesSection
            [items]="config.diseases"
            findingType="DISEASE"
            [viewOnly]="viewOnly"
            [tenantId]="helper.getTenantId()"
            [reportId]="reportId ?? ''"
            [preloadedFindings]="preloadedFindings"
            (changed)="onFindingsChanged()">
          </app-findings-section>
        </div>
        <div *ngIf="!inspectionService.config()" class="text-sm text-gray-400 italic py-4">No items configured for this field type.</div>
      </div>
    </mat-step>

    <!-- Step 5: Deficiencies -->
    <mat-step label="Deficiencies">
      <div class="px-4 py-4 overflow-y-auto">
        <p class="text-sm font-semibold text-gray-600 border-b pb-2 mb-4 uppercase tracking-wide">Deficiencies</p>
        <div *ngIf="inspectionService.config() as config">
          <app-findings-section
            #deficienciesSection
            [items]="config.deficiencies"
            findingType="DEFICIENCY"
            [viewOnly]="viewOnly"
            [tenantId]="helper.getTenantId()"
            [reportId]="reportId ?? ''"
            [preloadedFindings]="preloadedFindings"
            (changed)="onFindingsChanged()">
          </app-findings-section>
        </div>
        <div *ngIf="!inspectionService.config()" class="text-sm text-gray-400 italic py-4">No items configured for this field type.</div>
      </div>
    </mat-step>

  </mat-stepper>

  <!-- ── Sticky bottom navigation bar ─────────────────────────────────── -->
  <div style="display:flex; align-items:center; justify-content:space-between; padding:10px 16px; border-top:1px solid #e0e0e0; background:#fff; flex-shrink:0; gap:8px;">

    <!-- Left group: Close + Analysis icon -->
    <div style="display:flex; align-items:center; gap:8px;">
      <button mat-stroked-button (click)="onCloseClick()">
        <mat-icon>close</mat-icon>
        Close
      </button>
      <button
        mat-icon-button
        (click)="openAnalysisPanel()"
        matTooltip="View Analysis Data"
        style="color:#555;">
        <mat-icon>bar_chart</mat-icon>
      </button>
      <!-- Re-send PDF — finalised reports only, and Inspector only, matching the report list and
           the API. Not viewOnly: a draft opens read-only for a requestor or viewer too. -->
      <button
        *ngIf="reportFinalised && reportId && helper.canEditInspection()"
        mat-icon-button
        [disabled]="retriggeringPdf"
        (click)="retriggerPdf()"
        matTooltip="Re-send PDF report by email"
        style="color:#555;">
        <mat-icon>{{ retriggeringPdf ? 'hourglass_empty' : 'forward_to_inbox' }}</mat-icon>
      </button>
    </div>

    <!-- Right group: Back + Save Draft + Next/Finalise -->
    <div style="display:flex; align-items:center; gap:8px;">

      <button
        mat-stroked-button
        *ngIf="currentStepIndex > 0"
        (click)="back()">
        <mat-icon>arrow_back</mat-icon>
        Back
      </button>

      <button
        mat-stroked-button
        *ngIf="!viewOnly"
        [disabled]="form.invalid || savingDraft || uploadingPhoto"
        (click)="saveDraft()">
        {{ savingDraft ? 'Saving...' : 'Save Draft' }}
      </button>

      <button
        *ngIf="!isLastStep"
        mat-flat-button
        color="primary"
        (click)="next()">
        Next
        <mat-icon>arrow_forward</mat-icon>
      </button>

      <button
        *ngIf="isLastStep && !viewOnly && isInspector"
        mat-flat-button
        color="accent"
        [disabled]="finalisingReport || savingDraft"
        (click)="finalise()">
        {{ finalisingReport ? 'Finalising...' : 'Finalise Report' }}
      </button>

    </div>
  </div>

</div>
